import React, { useState } from 'react';
import ModerationQueue from '../../components/moderator/ModerationQueue';
import { createAuthenticatedApiClient } from '../../config/api';

// Define interface for moderation note (same as in ModerationQueue component)
interface ModerationNote {
  _id: string;
  note: string;
  status: 'pending' | 'ai-approved' | 'moderator-approved' | 'rejected';
  isFlagged: boolean;
  flagCount: number;
  createdAt: string;
  updatedAt: string;
  contributorInfo: {
    userId: string;
    aliasName: string;
  };
  qualityScore?: number;
  moderation?: {
    aiDecision?: string;
    ai_review_notes?: string;
    moderatorComment?: string;
  };
}

interface QueueExportResponse {
  success: boolean;
  data: ModerationNote[];
}

/**
 * ModeratorQueuePage - Main page for the moderation queue interface
 * Accessible at /moderator/queue
 * Provides moderators with ability to review, filter, and take action on pending notes
 */
const ModeratorQueuePage: React.FC = () => {
  const [isExporting, setIsExporting] = useState(false);

  // Handle quick actions from the queue component
  const handleQuickAction = (noteId: string, action: 'approve' | 'reject') => {
    // This could trigger notifications, analytics, or other side effects
    console.log(`Note ${noteId} was ${action}ed via quick action`);
    
    // Could implement toast notifications here
    // showNotification(`Note successfully ${action}ed`, 'success');
  };

  // Export current moderation queue to CSV
  const handleExportQueue = async () => {
    try {
      setIsExporting(true);
      
      const apiClient = createAuthenticatedApiClient();
      let allNotes: ModerationNote[] = [];
      let currentPage = 1;
      let hasMorePages = true;
      const maxLimit = 100; // Backend limit is 100 records per request
      
      // Fetch all pages of data
      while (hasMorePages) {
        const response = await apiClient.get<QueueExportResponse>(
          `/moderator/notes/queue?status=pending&page=${currentPage}&limit=${maxLimit}`
        );
        
        if (response.data.success) {
          const pageData = response.data.data;
          allNotes = [...allNotes, ...pageData];
          
          // Check if there are more pages (assuming the API returns pagination info)
          // If the returned data is less than the limit, we've reached the last page
          hasMorePages = pageData.length === maxLimit;
          currentPage++;
        } else {
          throw new Error('Failed to fetch queue data for export');
        }
        
        // Safety break to prevent infinite loop
        if (currentPage > 50) { // Max 5000 records (50 pages * 100 per page)
          console.warn('Export stopped at 50 pages to prevent infinite loop');
          break;
        }
      }
      
      console.log(`Fetched ${allNotes.length} notes across ${currentPage - 1} pages`);
      
      // Convert notes to CSV format
      const csvHeaders = [
        'Note ID',
        'Content',
        'Status', 
        'Flagged',
        'Flag Count',
        'Quality Score',
        'AI Decision',
        'Contributor Alias',
        'Created Date',
        'Updated Date'
      ];
      
      const csvRows = allNotes.map((note: ModerationNote) => [
        note._id,
        `"${note.note.replace(/"/g, '""')}"`, // Escape quotes in content
        note.status,
        note.isFlagged ? 'Yes' : 'No',
        note.flagCount || 0,
        note.qualityScore || 'N/A',
        note.moderation?.aiDecision || 'N/A',
        note.contributorInfo?.aliasName || 'Anonymous',
        new Date(note.createdAt).toLocaleDateString(),
        new Date(note.updatedAt).toLocaleDateString()
      ]);
      
      // Create CSV content
      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map((row: (string | number)[]) => row.join(','))
      ].join('\n');
      
      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `moderation-queue-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`Successfully exported ${allNotes.length} notes to CSV`);
    } catch (error) {
      console.error('Error exporting queue:', error);
      alert('Failed to export queue. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      {/* Page Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="h3 mb-1">Moderation Queue</h1>
              <p className="text-muted mb-0">
                Review and moderate pending notes submitted by contributors.
              </p>
            </div>
            
            {/* Optional toolbar - could add export, bulk actions, etc. */}
            <div className="d-flex gap-2">
              <button 
                className="btn btn-outline-secondary btn-sm"
                onClick={handleExportQueue}
                disabled={isExporting}
              >
                <i className={`fas ${isExporting ? 'fa-spinner fa-spin' : 'fa-download'} me-1`}></i>
                {isExporting ? 'Exporting...' : 'Export Queue'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Moderation Queue Component */}
      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <ModerationQueue onQuickAction={handleQuickAction} />
            </div>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="alert alert-info">
            <h6 className="alert-heading">
              <i className="fas fa-info-circle me-2"></i>
              Moderation Guidelines
            </h6>
            <ul className="mb-0 small">
              <li><strong>Pending:</strong> Notes awaiting human review</li>
              <li><strong>AI Approved:</strong> Notes approved by AI that may need verification</li>
              <li><strong>Flagged:</strong> Notes reported by users for review</li>
              <li><strong>Quality Score:</strong> AI-generated content quality rating (0-100)</li>
              <li>Click the eye icon to view full note details and advanced moderation options</li>
              <li>Use quick approve/reject buttons for simple decisions</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModeratorQueuePage; 